import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { Star, MapPin, Gift, CheckCircle, Loader2, Heart, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const StarRating = ({ value, onChange, label, size = 'default' }) => {
  const [hovered, setHovered] = useState(0);
  const starSize = size === 'large' ? 'w-10 h-10' : 'w-7 h-7';
  
  return (
    <div className="space-y-2">
      {label && <Label className="text-sm text-muted-foreground">{label}</Label>}
      <div className="flex gap-1">
        {[1, 2, 3, 4, 5].map((star) => (
          <button
            key={star}
            type="button"
            className="focus:outline-none transition-transform hover:scale-110"
            onMouseEnter={() => setHovered(star)}
            onMouseLeave={() => setHovered(0)}
            onClick={() => onChange(star)}
          >
            <Star
              className={`${starSize} transition-colors ${
                star <= (hovered || value)
                  ? 'fill-yellow-400 text-yellow-400'
                  : 'text-gray-300'
              }`}
            />
          </button>
        ))}
      </div>
    </div>
  );
};

const CustomerFeedback = () => {
  const { token } = useParams();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState(null);
  const [submitted, setSubmitted] = useState(false);
  
  const [feedback, setFeedback] = useState({
    rating: 0,
    experience_rating: 0,
    therapist_rating: 0,
    cleanliness_rating: 0,
    comments: '',
    would_recommend: true
  });

  useEffect(() => {
    fetchStatus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const fetchStatus = async () => {
    try {
      const response = await axios.get(`${API}/rewards/feedback-status/${token}`);
      setStatus(response.data);
      if (response.data.feedback_submitted) {
        setSubmitted(true);
      }
    } catch (error) {
      console.error('Error fetching status:', error);
      toast.error('Invalid or expired feedback link');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (feedback.rating === 0) {
      toast.error('Please provide an overall rating');
      return;
    }
    
    setSubmitting(true);
    try {
      const response = await axios.post(`${API}/rewards/submit-feedback/${token}`, feedback);
      setSubmitted(true);
      
      if (response.data.reward_earned) {
        toast.success('Congratulations! You earned a 5% discount on your next visit!');
      } else {
        toast.success('Thank you for your feedback!');
      }
      
      // Refresh status
      fetchStatus();
    } catch (error) {
      console.error('Error submitting feedback:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit feedback');
    } finally {
      setSubmitting(false);
    }
  };

  const handleGoogleMapsClick = () => {
    // The tracking link will automatically redirect
    window.open(`${API}/rewards/track-maps/${token}`, '_blank');
    // Refresh status after a delay
    setTimeout(fetchStatus, 2000);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!status) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full text-center">
          <CardContent className="pt-8 pb-8">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">😔</span>
            </div>
            <h2 className="text-xl font-semibold mb-2">Link Expired</h2>
            <p className="text-muted-foreground">
              This feedback link is no longer valid. Please contact the spa for assistance.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-amber-50 to-orange-50 py-8 px-4">
      <div className="max-w-lg mx-auto space-y-6">
        {/* Header */}
        <div className="text-center space-y-2">
          <div className="w-16 h-16 bg-primary/10 rounded-full flex items-center justify-center mx-auto">
            <Sparkles className="w-8 h-8 text-primary" />
          </div>
          <h1 className="text-2xl font-serif text-foreground">
            Thank you, {status.guest_name}!
          </h1>
          <p className="text-muted-foreground">
            We hope you enjoyed your visit to {status.property_name}
          </p>
        </div>

        {/* Reward Banner */}
        <Card className="bg-gradient-to-r from-amber-100 to-orange-100 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <Gift className="w-10 h-10 text-amber-600" />
              <div>
                <p className="font-semibold text-amber-900">Earn 5% OFF your next visit!</p>
                <p className="text-sm text-amber-700">
                  Complete both steps below to unlock your discount
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Progress Steps */}
        <div className="grid grid-cols-2 gap-4">
          {/* Step 1: Google Maps */}
          <Card className={`${status.google_maps_clicked ? 'border-green-500 bg-green-50' : ''}`}>
            <CardContent className="p-4 text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
                status.google_maps_clicked ? 'bg-green-500' : 'bg-gray-200'
              }`}>
                {status.google_maps_clicked ? (
                  <CheckCircle className="w-6 h-6 text-white" />
                ) : (
                  <MapPin className="w-6 h-6 text-gray-500" />
                )}
              </div>
              <p className="font-medium text-sm">Step 1</p>
              <p className="text-xs text-muted-foreground mb-3">Rate us on Google</p>
              {!status.google_maps_clicked && status.google_maps_link && (
                <Button 
                  size="sm" 
                  onClick={handleGoogleMapsClick}
                  className="w-full"
                  data-testid="google-maps-btn"
                >
                  <MapPin className="w-4 h-4 mr-1" />
                  Open Maps
                </Button>
              )}
              {status.google_maps_clicked && (
                <p className="text-xs text-green-600 font-medium">Completed!</p>
              )}
            </CardContent>
          </Card>

          {/* Step 2: Feedback */}
          <Card className={`${status.feedback_submitted ? 'border-green-500 bg-green-50' : ''}`}>
            <CardContent className="p-4 text-center">
              <div className={`w-12 h-12 rounded-full flex items-center justify-center mx-auto mb-2 ${
                status.feedback_submitted ? 'bg-green-500' : 'bg-gray-200'
              }`}>
                {status.feedback_submitted ? (
                  <CheckCircle className="w-6 h-6 text-white" />
                ) : (
                  <Heart className="w-6 h-6 text-gray-500" />
                )}
              </div>
              <p className="font-medium text-sm">Step 2</p>
              <p className="text-xs text-muted-foreground mb-3">Share feedback</p>
              {status.feedback_submitted ? (
                <p className="text-xs text-green-600 font-medium">Completed!</p>
              ) : (
                <p className="text-xs text-muted-foreground">Fill form below</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Reward Earned Banner */}
        {status.reward_earned && (
          <Card className="bg-gradient-to-r from-green-100 to-emerald-100 border-green-300">
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                  <Gift className="w-6 h-6 text-white" />
                </div>
                <div>
                  <p className="font-semibold text-green-900">
                    🎉 You've earned {status.discount_percent}% OFF!
                  </p>
                  <p className="text-sm text-green-700">
                    Your discount will be auto-applied on your next visit
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Feedback Form */}
        {!submitted && (
          <Card>
            <CardHeader>
              <CardTitle>Share Your Experience</CardTitle>
              <CardDescription>
                Your feedback helps us serve you better
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Overall Rating */}
                <div className="text-center">
                  <Label className="block mb-3">Overall Experience</Label>
                  <div className="flex justify-center">
                    <StarRating
                      value={feedback.rating}
                      onChange={(v) => setFeedback({ ...feedback, rating: v })}
                      size="large"
                    />
                  </div>
                </div>

                <div className="border-t pt-4 space-y-4">
                  {/* Individual Ratings */}
                  <div className="grid grid-cols-1 gap-4">
                    <StarRating
                      label="Therapist Service"
                      value={feedback.therapist_rating}
                      onChange={(v) => setFeedback({ ...feedback, therapist_rating: v })}
                    />
                    <StarRating
                      label="Ambiance & Cleanliness"
                      value={feedback.cleanliness_rating}
                      onChange={(v) => setFeedback({ ...feedback, cleanliness_rating: v })}
                    />
                    <StarRating
                      label="Overall Experience"
                      value={feedback.experience_rating}
                      onChange={(v) => setFeedback({ ...feedback, experience_rating: v })}
                    />
                  </div>
                </div>

                {/* Would Recommend */}
                <div>
                  <Label className="block mb-2">Would you recommend us?</Label>
                  <RadioGroup
                    value={feedback.would_recommend ? "yes" : "no"}
                    onValueChange={(v) => setFeedback({ ...feedback, would_recommend: v === "yes" })}
                    className="flex gap-4"
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="yes" id="recommend-yes" />
                      <Label htmlFor="recommend-yes">Yes, definitely!</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="no" id="recommend-no" />
                      <Label htmlFor="recommend-no">Not really</Label>
                    </div>
                  </RadioGroup>
                </div>

                {/* Comments */}
                <div>
                  <Label htmlFor="comments">Additional Comments (Optional)</Label>
                  <Textarea
                    id="comments"
                    value={feedback.comments}
                    onChange={(e) => setFeedback({ ...feedback, comments: e.target.value })}
                    placeholder="Tell us what you loved or how we can improve..."
                    rows={3}
                    className="mt-1"
                  />
                </div>

                <Button 
                  type="submit" 
                  className="w-full" 
                  disabled={submitting || feedback.rating === 0}
                  data-testid="submit-feedback-btn"
                >
                  {submitting ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting...</>
                  ) : (
                    <>Submit Feedback</>
                  )}
                </Button>
              </form>
            </CardContent>
          </Card>
        )}

        {/* Thank You Message */}
        {submitted && (
          <Card className="text-center">
            <CardContent className="pt-8 pb-8">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-green-600" />
              </div>
              <h2 className="text-xl font-semibold mb-2">Thank You!</h2>
              <p className="text-muted-foreground">
                Your feedback has been submitted successfully.
                {status.reward_earned && " Don't forget to use your 5% discount on your next visit!"}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          Powered by WellNest Technologies
        </p>
      </div>
    </div>
  );
};

export default CustomerFeedback;
